import React from 'react';
import { Link } from 'react-router-dom';
import { ClientData, HeroContent, HighlightContent, InsightCardContent } from '../types';

interface ClientLandingPageProps {
  clientData: ClientData;
}

const getHeroStyles = (hero?: HeroContent) => {
  if (!hero?.backgroundImage) {
    return {
      background: 'linear-gradient(135deg, rgba(15,118,110,0.85), rgba(6,182,212,0.85))',
    };
  }
  return {
    backgroundImage: `linear-gradient(135deg, rgba(11,15,25,0.65), rgba(8,15,23,0.35)), url(${hero.backgroundImage})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  };
};

const isExternal = (url: string) => /^https?:\/\//i.test(url);
const CTAButton: React.FC<{ label?: string; url?: string; className?: string; style?: React.CSSProperties }> = ({
  label,
  url,
  className,
  style,
}) => {
  if (!label || !url) return null;
  if (url.startsWith('#')) {
    return (
      <a href={url} className={className} style={style}>
        {label}
        <svg className="ml-2 h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 5l7 7-7 7" />
        </svg>
      </a>
    );
  }
  if (isExternal(url)) {
    return (
      <a href={url} className={className} style={style} target="_blank" rel="noopener noreferrer">
        {label}
        <svg className="ml-2 h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 5l7 7-7 7" />
        </svg>
      </a>
    );
  }
  return (
    <Link to={url} className={className} style={style}>
      {label}
      <svg className="ml-2 h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 5l7 7-7 7" />
      </svg>
    </Link>
  );
};

const InsightCard: React.FC<{ card?: InsightCardContent; defaultTitle: string; themeColor: string }> = ({
  card,
  defaultTitle,
  themeColor,
}) => {
  const hasBullets = (card?.bulletPoints || []).length > 0;
  return (
    <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-md p-6 border border-white/20">
      <div className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-2">{card?.subtitle}</div>
      <h3 className="text-2xl font-bold text-gray-900 mb-4">{card?.title || defaultTitle}</h3>
      {card?.body && <p className="text-gray-700 leading-relaxed mb-4">{card.body}</p>}
      {hasBullets && (
        <ul className="space-y-2 mb-4">
          {card?.bulletPoints?.map((item, idx) => (
            <li key={idx} className="flex items-start text-sm text-gray-700">
              <span className="mt-1 mr-2 h-1.5 w-1.5 rounded-full" style={{ backgroundColor: themeColor }} />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      )}
      <CTAButton
        label={card?.ctaLabel}
        url={card?.ctaUrl}
        className="inline-flex items-center text-sm font-semibold"
        style={{ color: themeColor }}
      />
    </div>
  );
};

const HighlightBanner: React.FC<{ highlight?: HighlightContent; fallbackTitle: string; themeColor: string }> = ({
  highlight,
  fallbackTitle,
  themeColor,
}) => {
  if (!highlight?.title && !highlight?.description) {
    return (
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg p-10 border border-white/20">
        <div className="text-sm uppercase tracking-wide text-gray-500 mb-3">Key Insight</div>
        <h2 className="text-3xl md:text-4xl font-bold text-gray-900">Leverage data-backed insights to guide your next move.</h2>
      </div>
    );
  }
  return (
    <div
      className="rounded-2xl shadow-lg p-10 text-white relative overflow-hidden"
      style={{
        background: highlight?.backgroundImage
          ? `linear-gradient(135deg, rgba(30,41,59,0.85), rgba(15,23,42,0.65)), url(${highlight.backgroundImage})`
          : `linear-gradient(135deg, ${themeColor}, rgba(14,116,144,0.95))`,
        backgroundSize: highlight?.backgroundImage ? 'cover' : undefined,
        backgroundPosition: 'center',
      }}
    >
      {highlight?.eyebrow && (
        <div className="text-xs md:text-sm uppercase tracking-[0.4em] text-white/70 mb-4">{highlight.eyebrow}</div>
      )}
      <h2 className="text-3xl md:text-4xl font-bold mb-4">
        {highlight?.title || fallbackTitle}
      </h2>
      {highlight?.description && (
        <p className="text-base md:text-lg text-white/90 max-w-3xl leading-relaxed mb-6">
          {highlight.description}
        </p>
      )}
      {highlight?.supportingPoints && highlight.supportingPoints.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {highlight.supportingPoints.map((point, idx) => (
            <div key={idx} className="bg-white/10 rounded-lg p-4 border border-white/10">
              <div className="text-2xl font-bold text-white">{point.value}</div>
              <div className="text-sm text-white/80 mt-2">{point.label}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default function ClientLandingPage({ clientData }: ClientLandingPageProps) {
  const themeColor = clientData.uiSettings?.theme?.primary || '#14B8A6';
  const landingCards = clientData.uiSettings?.landingCards;
  const showSeoSummary = landingCards?.seoSummary !== false;
  const showResearchSummary = landingCards?.researchSummary !== false;
  const landing = clientData.pageContent?.landing;
  const hero = landing?.hero;
  const heroStyles = getHeroStyles(hero);
  const laLogo = 'https://lhijeqvswxugvtfwkoyl.supabase.co/storage/v1/object/public/client-files/littlefield-logo.png';

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      <section className="relative overflow-hidden">
        <div className="min-h-[60vh] flex items-center" style={heroStyles}>
          <div className="container mx-auto px-6 py-20">
            <div className="mb-6">
              <a
                href="https://littlefieldagency.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex"
              >
                <img
                  src={laLogo}
                  alt="Littlefield Agency"
                  className="h-10 w-auto object-contain drop-shadow-lg"
                />
              </a>
            </div>
            {hero?.eyebrow && (
              <div className="text-xs md:text-sm uppercase tracking-[0.4em] text-white/70 mb-4">{hero.eyebrow}</div>
            )}
            <div className="max-w-3xl">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
                {hero?.title || clientData.headline}
              </h1>
              {/* <p className="text-lg md:text-xl text-white/90 max-w-2xl leading-relaxed mb-6">
                {hero?.subtitle || clientData.introduction}
              </p> */}
              {/* {hero?.description && (
                <p className="text-base text-white/80 max-w-2xl leading-relaxed mb-6">
                  {hero.description}
                </p>
              )} */}
              <CTAButton
                label={hero?.ctaLabel}
                url={hero?.ctaUrl}
                className="inline-flex items-center bg-white text-gray-900 px-6 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transition-shadow"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="relative -mt-16 z-10">
        <div className="container mx-auto px-6 pb-20">
          <HighlightBanner
            highlight={landing?.keyInsight}
            fallbackTitle="Translate research into action with a focused insight your team can rally around."
            themeColor={themeColor}
          />

          {(showSeoSummary || showResearchSummary) && (
            <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-8">
              {showSeoSummary && (
                <InsightCard
                  card={landing?.seoSummary}
                  defaultTitle="SEO/AEO/GEO Insights"
                  themeColor={themeColor}
                />
              )}
              {showResearchSummary && (
                <InsightCard
                  card={landing?.researchSummary}
                  defaultTitle="Research Insights"
                  themeColor={themeColor}
                />
              )}
            </div>
          )}

        </div>
      </section>
      <footer className="bg-slate-900/80 border-t border-white/10">
        <div className="container mx-auto px-6 py-12 text-center text-white/70 text-sm">
          <img
            src="https://lhijeqvswxugvtfwkoyl.supabase.co/storage/v1/object/public/client-files/littlefield-logo.png"
            alt="Littlefield Agency"
            className="w-[200px] h-auto object-contain mx-auto mb-2"
          />
          <p>Powered by Littlefield Agency | Customer insights for {clientData.name}</p>
        </div>
      </footer>
    </div>
  );
}
